import React, { useState } from 'react'
import Papa from 'papaparse'
import {
  StatisticsColumnCompared,
  StatisticsDonut,
  StatisticsLinesChart
} from '../../components/Statistics'
import { CampaignLinesChart } from './components/CampaignLinesChart'

// Descarga cliente-side de un array de objetos como CSV (mismo patrón que
// Results.js / Send.js: Papa.unparse + Blob + <a download>).
const downloadCsv = (rows, filename) => {
  if (!rows || rows.length === 0) return
  const csv = Papa.unparse(rows)
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// Formatea "2026-08-05T11:00:22.826Z" → "2026-08-05 11:00:22" para lectura rápida.
// Timestamps en prod-deliveries están en UTC (ISO 8601 con Z). Los pintamos en
// hora Europe/Madrid (aplica DST automático) para que coincida con la hora real
// del envío en España. Formato: "YYYY-MM-DD HH:mm:ss".
const MADRID_TZ = 'Europe/Madrid'
const fmtTs = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  const parts = new Intl.DateTimeFormat('es-ES', {
    timeZone: MADRID_TZ,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false
  }).formatToParts(d).reduce((acc, p) => (acc[p.type] = p.value, acc), {})
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`
}

const REASON_STYLE = {
  'Número inválido': 'bg-red-100 text-red-800',
  'Rechazo del gateway': 'bg-rose-100 text-rose-800',
  'No entregable (rechazo del operador)': 'bg-orange-100 text-orange-800',
  'Sin cobertura o apagado': 'bg-yellow-100 text-yellow-800',
  'Sin confirmación del operador': 'bg-slate-100 text-slate-700',
  'No entregable': 'bg-gray-100 text-gray-800'
}

const SmsFailedTable = ({ details, campaignName }) => {
  const [open, setOpen] = useState(false)
  if (!details || details.length === 0) return null
  const handleDownload = () => {
    // Timestamps se exportan en hora Europe/Madrid; el nombre de columna lo
    // indica explícitamente para que quien abra el CSV no tenga que preguntar.
    const rows = details.map((d) => ({
      telefono: d.phone,
      motivo: d.reason || 'No entregable',
      sub_status: d.subStatus || '',
      partes: d.parts || '',
      enviado_madrid: fmtTs(d.sent),
      fallo_notificado_madrid: fmtTs(d.failed),
      fallback_desde: d.fallbackFrom || '',
      msgId: d.msgId || ''
    }))
    const safe = (campaignName || 'campaña').replace(/[^\w\-]+/g, '_')
    downloadCsv(rows, `errores_sms_${safe}_horas_madrid.csv`)
  }
  return (
    <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 hover:ring-indigo-200 hover:shadow-md transition-all duration-200 p-6">
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-2 text-base font-semibold text-grey-blue hover:text-indigo-700 text-left"
        >
          <span className={`transition-transform text-sm ${open ? 'rotate-90' : ''}`}>▶</span>
          SMS con fallo reportado por el proveedor ({details.length})
        </button>
        <button
          type="button"
          onClick={handleDownload}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
        >
          ⬇ Descargar CSV
        </button>
      </div>
      {open && (
        <div className="mt-4 max-h-[500px] overflow-auto rounded border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr className="text-left text-gray-600">
                <th className="px-4 py-2.5 font-semibold">Teléfono</th>
                <th className="px-4 py-2.5 font-semibold">Motivo</th>
                <th className="px-4 py-2.5 font-semibold">Enviado <span className="text-[10px] font-normal text-gray-400">(Madrid)</span></th>
                <th className="px-4 py-2.5 font-semibold">Fallo notificado <span className="text-[10px] font-normal text-gray-400">(Madrid)</span></th>
                <th className="px-4 py-2.5 font-semibold">msgId</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {details.map((d) => (
                <tr key={d.msgId} className="hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono">{d.phone}</td>
                  <td className="px-4 py-2">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${REASON_STYLE[d.reason] || REASON_STYLE['No entregable']}`}>
                      {d.reason || 'No entregable'}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-gray-600">{fmtTs(d.sent)}</td>
                  <td className="px-4 py-2 text-red-700">{fmtTs(d.failed)}</td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">{(d.msgId || '').slice(0, 12)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

const getSmsData = (campaignInfo) => {
  const sent = campaignInfo?.sms?.sent ?? 0
  const delivered = campaignInfo?.sms?.delivered ?? 0
  const failed = campaignInfo?.sms?.failed ?? 0
  // "sin_ack" = enviado, sin delivered, sin failed. Cierra el hueco visual
  // (sent = delivered + failed + sin_ack) para que no parezca que se pierden.
  const sinAck = Math.max(0, sent - delivered - failed)
  return [
    { type: 'sent', value: sent },
    { type: 'delivered', value: delivered },
    { type: 'failed', value: failed },
    { type: 'sin_ack', value: sinAck }
  ]
}
// Resumen multipart: partes reales (Σ smsCount) y contactos desduplicados.
// parts_* vienen desglosados por estado desde el agregador, así cuadra 1:1 con
// el portal de Cytech por campaña (entregadas + fallidas + pendientes = total).
const getSmsParts = (campaignInfo) => {
  const sms = campaignInfo?.sms || {}
  const parts = sms.parts ?? 0
  // Contactos realmente enviados y desduplicados. Denominador correcto del ACK:
  // NO usamos `sent` (infra-cuenta cuando un submit-fail no escribe la clave
  // `sent`); `contacts` cuenta 1 fila ganadora por teléfono realmente enviada.
  const contacts = sms.contacts ?? sms.sent ?? 0
  const delivered = sms.delivered ?? 0
  const failed = sms.failed ?? 0
  // Pendiente = enviado sin ACK final (ni delivered ni failed). Cierra el 100%:
  // delivered + failed + pending = contacts.
  const pending = Math.max(0, contacts - delivered - failed)
  const partsDelivered = sms.parts_delivered ?? 0
  const partsFailed = sms.parts_failed ?? 0
  const partsSent = sms.parts_sent ?? parts
  const partsPending = Math.max(0, partsSent - partsDelivered - partsFailed)
  const perContact = contacts > 0 ? (parts / contacts) : 0
  // % ACK reales sobre contactos enviados (0 si no hay envíos, nunca NaN).
  const pct = (n) => (contacts > 0 ? +((n / contacts) * 100).toFixed(1) : 0)
  return {
    parts, contacts, delivered, failed, pending,
    partsDelivered, partsFailed, partsPending, perContact,
    deliveredPct: pct(delivered), failedPct: pct(failed), pendingPct: pct(pending)
  }
}

const KpiTile = ({ label, value, sub, accent }) => (
  <div
    className={`rounded-lg border px-4 py-3 ${
      accent
        ? 'bg-emerald-50 border-emerald-100'
        : 'bg-gray-50 border-gray-100'
    }`}
  >
    <div
      className={`text-[11px] font-semibold uppercase tracking-wide ${
        accent ? 'text-emerald-700' : 'text-gray-500'
      }`}
    >
      {label}
    </div>
    <div
      className={`text-2xl font-bold leading-tight ${
        accent ? 'text-emerald-600' : 'text-grey-blue'
      }`}
    >
      {value}
    </div>
    {sub && <div className="text-[11px] text-gray-500 mt-0.5 leading-snug">{sub}</div>}
  </div>
)

const SmsKpis = ({ campaignInfo }) => {
  const p = getSmsParts(campaignInfo)
  const nf = (n) => (n ?? 0).toLocaleString('es-ES')
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
      <KpiTile
        label="Partes (multipart)"
        value={nf(p.parts)}
        sub={`Ent. ${nf(p.partsDelivered)} · Fall. ${nf(p.partsFailed)} · Pend. ${nf(p.partsPending)}`}
      />
      <KpiTile
        label="Contactos únicos"
        value={nf(p.contacts)}
        sub={`≈ ${p.perContact.toFixed(1)} partes/contacto`}
      />
      <KpiTile
        label="ACK entregado"
        value={`${p.deliveredPct}%`}
        sub={`Fallido ${p.failedPct}% · Pendiente ${p.pendingPct}%`}
        accent
      />
    </div>
  )
}

// KPIs de RBM, simétricos a los de SMS para que la tarjeta no quede con hueco.
// deliveredEffective corrige el subreporte del `delivered` de Orange (cuenta
// como entregado si hay delivered OR displayed OR click).
const getRbmKpis = (campaignInfo) => {
  const rbm = campaignInfo?.rbm || {}
  const sent = rbm.sent ?? 0
  // Usar `delivered` CRUDO (el mismo que pinta la barra "Entregados"), NO
  // `deliveredEffective` (que cuenta también vistos/clicks y daba ~100% aunque
  // la barra mostrara 99,94%). Así KPI y gráfico coinciden.
  const delivered = rbm.delivered ?? 0
  const displayed = rbm.displayed ?? 0
  const notDelivered = Math.max(0, sent - delivered)
  const pct = (n) => (sent > 0 ? +((n / sent) * 100).toFixed(2) : 0)
  return {
    sent,
    delivered,
    displayed,
    notDelivered,
    deliveredPct: pct(delivered),
    displayedPct: pct(displayed),
    notDeliveredPct: pct(notDelivered)
  }
}

const RbmKpis = ({ campaignInfo }) => {
  const r = getRbmKpis(campaignInfo)
  const nf = (n) => (n ?? 0).toLocaleString('es-ES')
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
      <KpiTile label="Contactos únicos" value={nf(r.sent)} sub="Alcance RBM" />
      <KpiTile
        label="Entregado"
        value={`${r.deliveredPct}%`}
        sub={`No entregado ${nf(r.notDelivered)} (${r.notDeliveredPct}%)`}
        accent
      />
      <KpiTile
        label="Visualizado"
        value={`${r.displayedPct}%`}
        sub={`${nf(r.displayed)} vistos`}
      />
    </div>
  )
}

const getRbmData = (campaignInfo) => {
  return [
    {
      type: 'sent',
      value: campaignInfo?.rbm?.sent ?? 0
    },
    {
      type: 'delivered',
      value: campaignInfo?.rbm?.delivered ?? 0
    },
    {
      type: 'visualiced',
      value: campaignInfo?.rbm?.displayed ?? 0
    },
    {
      // Contactos que pulsaron, escribieron o avanzaron en el flujo tras el init.
      type: 'interacted',
      value: campaignInfo?.rbm?.interacted ?? 0
    }
  ]
}

const getUnsubscriptionsData = (campaignInfo) => {
  const totalDeliveries =
    (campaignInfo?.rbm?.delivered ?? 0) + (campaignInfo?.sms?.delivered ?? 0)
  return [
    {
      type: 'Deliveries',
      value: totalDeliveries
    },
    {
      type: 'SMS Unsubscriptions',
      value: campaignInfo?.optOutInfo?.sms ?? 0
    },
    {
      type: 'RBM Unsubscriptions',
      value: campaignInfo?.optOutInfo?.rbm ?? 0
    }
  ]
}

// Helper function to check if there are any statistics available
const hasStatistics = (campaignInfo) => {
  if (!campaignInfo) return false
  
  // Check SMS stats
  const hasSmsStats = (campaignInfo?.sms?.sent ?? 0) > 0 || (campaignInfo?.sms?.delivered ?? 0) > 0
  
  // Check RBM stats
  const hasRbmStats = 
    (campaignInfo?.rbm?.sent ?? 0) > 0 || 
    (campaignInfo?.rbm?.delivered ?? 0) > 0 || 
    (campaignInfo?.rbm?.displayed ?? 0) > 0
  
  // Check date stats
  const hasDateStats = campaignInfo?.dateStats && 
    typeof campaignInfo.dateStats === 'object' &&
    Object.keys(campaignInfo.dateStats).length > 0 &&
    Object.values(campaignInfo.dateStats).some(stats => 
      stats && typeof stats === 'object' && Object.keys(stats).length > 0
    )
  
  return hasSmsStats || hasRbmStats || hasDateStats
}

// Empty State Component
const EmptyStatisticsState = ({ campaignName }) => {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6">
      <div className="bg-gray-100 rounded-full p-6 mb-6">
        <svg
          className="w-16 h-16 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
          />
        </svg>
      </div>
      <h2 className="text-2xl font-semibold text-grey-blue mb-2">
        No hay estadísticas disponibles
      </h2>
      <p className="text-gray-500 text-center max-w-md">
        {campaignName 
          ? `La campaña "${campaignName}" aún no tiene datos de estadísticas. Los datos aparecerán aquí una vez que la campaña comience a enviar mensajes.`
          : 'Esta campaña aún no tiene datos de estadísticas. Los datos aparecerán aquí una vez que la campaña comience a enviar mensajes.'}
      </p>
    </div>
  )
}

function generateData() {
  const data = []
  const categories = ['visualizations', 'deliveries', 'clicks']
  const startDate = new Date(2024, 0, 1) // 1/1/2024

  for (let i = 0; i < 20; i++) {
    const date = new Date(startDate)
    date.setDate(startDate.getDate() + i) // Incrementa el día para cada objeto

    categories.forEach((category) => {
      const value = Math.floor(Math.random() * 1000) // Genera un número aleatorio para cada categoría
      data.push({
        //format dd/mm/yyyy
        date: date.toLocaleDateString('en-GB'),
        category: category,
        value: value
      })
    })
  }
  return data
}

const data = generateData()

export const StatisticsGeneral = ({ campaignInfo }) => {
  // Check if there are any statistics
  const hasStats = hasStatistics(campaignInfo)
  
  // If no statistics, show empty state
  if (!hasStats) {
    return (
      <article className="flex flex-col gap-6">
        {/* Campaign Name Header */}
        {campaignInfo?.name && (
          <div className="mb-2">
            <h1 className="text-3xl font-bold text-grey-blue">
              {campaignInfo.name}
            </h1>
          </div>
        )}
        
        {/* Empty State */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-8">
          <EmptyStatisticsState campaignName={campaignInfo?.name} />
        </div>
      </article>
    )
  }
  
  return (
    <article className="flex flex-col gap-6">
      {/* Campaign Name Header */}
      {campaignInfo?.name && (
        <div className="mb-2">
          <h1 className="text-3xl font-bold text-grey-blue">
            {campaignInfo.name}
          </h1>
        </div>
      )}
      
      {/* Messages Status Section - Two separate cards */}
      <div id="messageStatus" className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* SMS Card */}
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 hover:ring-indigo-200 hover:shadow-md transition-all duration-200 p-6">
          <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
            <span className="inline-block w-1.5 h-6 rounded-full bg-gradient-to-b from-indigo-500 to-indigo-300" />
            <h2 className="text-xl font-semibold text-grey-blue">SMS Statistics</h2>
          </div>
          {/* KPIs: multipart real, contactos desduplicados y ACK real */}
          <SmsKpis campaignInfo={campaignInfo} />
          <div className="w-full h-[340px] mt-2">
            <StatisticsColumnCompared
              data={getSmsData(campaignInfo)}
              conversion={false}
            />
          </div>
        </div>

        {/* RBM Card */}
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 hover:ring-indigo-200 hover:shadow-md transition-all duration-200 p-6">
          <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100">
            <span className="inline-block w-1.5 h-6 rounded-full bg-gradient-to-b from-indigo-500 to-indigo-300" />
            <h2 className="text-xl font-semibold text-grey-blue">RBM Statistics</h2>
          </div>
          {/* KPIs RBM: contactos, entregado y visualizado (rellena el hueco) */}
          <RbmKpis campaignInfo={campaignInfo} />
          <div className="w-full h-[340px] mt-2">
            <StatisticsColumnCompared data={getRbmData(campaignInfo)} />
          </div>
        </div>
      </div>

      {/* Tabla de SMS con failed reportado por el proveedor (ancho completo) */}
      <SmsFailedTable
        details={campaignInfo?.smsFailedDetails}
        campaignName={campaignInfo?.name}
      />
      
      {/* Messages Statistics Over Time Card */}
      <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-200 hover:ring-indigo-200 hover:shadow-md transition-all duration-200 p-6">
        <CampaignLinesChart
          dateStats={campaignInfo.dateStats}
          campaignTimestamp={campaignInfo.timestamp}
          dateRange={campaignInfo.dateRange}
        />
      </div>
      {/* <section id="optOuts">
        <h1 className="text-3xl font-semibold text-grey-blue w-full border-b-4 border-grey-blue">
          Unsubscriptions
        </h1>
        <div
          id="OptOutStats"
          className="flex justify-between w-full h-auto mr-10"
        >
          <div className="felx flex-col self-center">
            <h1 className="text-2xl font-semibold text-grey-blue w-full border-grey-blue py-4">
              SMS Unsubscriptions{' '}
              <span className="text-[color:#A14242] text-3xl font-bold">
                {campaignInfo.optOutInfo.sms}
              </span>
            </h1>
            <h1 className="text-2xl font-semibold text-grey-blue w-full border-grey-blue py-4">
              SMS Unsubscriptions{' '}
              <span className="text-[color:#A14242] text-3xl font-bold">
                {campaignInfo.optOutInfo.rbm}
              </span>
            </h1>
          </div>
          <div className="w-full h-auto">
            <StatisticsDonut data={getUnsubscriptionsData(campaignInfo)} />
          </div>
        </div>
      </section> */}
    </article>
  )
}
